import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import Reveal from "@/components/7fcc/Reveal";
import SectionHeader from "@/components/7fcc/SectionHeader";
import { HOME } from "@/data/homeContent";
import { EASE } from "@/lib/motion";

// Objection Library — all five tabs with their exact breakdowns from the
// original script.js. Tab 1 active by default; panel heading shows the
// active objection in curly quotes.
export default function ObjectionWidget() {
  const o = HOME.objections;
  const [active, setActive] = useState(0);
  const tab = o.tabs[active];

  return (
    <section id="objections" className="bg-ink-900 px-6 py-16 md:py-24" data-testid="home-objections">
      <div className="mx-auto max-w-4xl">
        <SectionHeader center eyebrow={o.eyebrow} title={o.headline} support={o.support} testId="home-objections-heading" />
        <Reveal delay={0.15} className="mt-10">
          <div
            className="flex flex-wrap justify-center gap-2.5"
            role="tablist"
            aria-label="Objection library"
            data-testid="objection-tabs"
          >
            {o.tabs.map((t, i) => (
              <button
                key={t.label}
                type="button"
                role="tab"
                aria-selected={active === i}
                onClick={() => setActive(i)}
                data-testid={`objection-tab-${i + 1}`}
                className={`rounded-full border px-4 py-2 text-xs font-semibold transition-all duration-300 md:text-sm ${
                  active === i
                    ? "border-brand bg-brand/10 text-fg shadow-glow-soft"
                    : "border-line bg-ink-950/50 text-fg-muted hover:border-brand/50 hover:text-fg"
                }`}
              >
                {t.label}
              </button>
            ))}
          </div>
        </Reveal>
        <Reveal delay={0.25} className="mt-8">
          <div className="rounded-lg border border-line bg-ink-950/60 p-7 md:p-9" data-testid="objection-panel">
            <AnimatePresence mode="wait">
              <motion.div
                key={active}
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -12 }}
                transition={{ duration: 0.3, ease: EASE }}
              >
                <h3 className="text-center font-display text-xl font-medium text-fg md:text-2xl" data-testid="objection-heading">
                  {tab.label}
                </h3>
                <div className="mt-7 space-y-7">
                  {[
                    [o.blockTitles[0], tab.mean, false],
                    [o.blockTitles[1], tab.weak, true],
                    [o.blockTitles[2], tab.strong, true],
                    [o.blockTitles[3], tab.follow, false],
                  ].map(([title, body], i) => (
                    <div key={title}>
                      <h4
                        className={`text-[11px] font-semibold uppercase tracking-[0.22em] ${
                          i === 0 || i === 2 ? "text-brand-light" : "text-silver-mid"
                        }`}
                        data-testid={`objection-block-title-${i + 1}`}
                      >
                        {title}
                      </h4>
                      <p className="mt-2 text-sm leading-relaxed text-fg-muted md:text-base" data-testid={`objection-block-body-${i + 1}`}>
                        {body}
                      </p>
                    </div>
                  ))}
                </div>
              </motion.div>
            </AnimatePresence>
          </div>
        </Reveal>
      </div>
    </section>
  );
}